<script setup>
/**
 * 时间趋势：一根柱子 = 一个时间桶。画哪个度量由页面给（成本 / 调用数 / token），一次只画一个 ——
 * 一张图两把尺子，两条线的高低对比全是假的。
 *
 * **柱子有宽度上限**：只有一个桶时，柱子撑满整张图的话，看的人以为那是一块背景板，不是数据。
 * **x 轴每一格都有标签**：按天给 `MM-DD`，按小时给 `HH:00`；y 轴的刻度由 echarts 按数据量级取，
 * 成本走 `¥`，其余走千分位整数（`cost-metrics.js` 是唯一的格式来源）。
 *
 * 含「成本待核算」调用的桶转成 warning 色 + 斜纹，条数在 tooltip 里写出来 —— 只靠颜色说事，
 * 色弱的人和打印出来的图上就什么都没有了。
 */
import { computed } from 'vue'
import EChart from '../../../components/EChart.vue'
import { categoryAxis, chartBase, chartTheme, valueAxis } from '../../../components/chart-theme.js'
import { useAdminTheme } from '../../../composables/useAdminTheme.js'
import { formatInt, formatMetric, formatMoney } from '../llm-format.js'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  /** @type {import('vue').PropType<import('../../../api/llm/index.js').CostTimeSlice[]>} */
  items: { type: Array, required: true },
  /** @type {import('vue').PropType<'supplier_cost_micros'|'calls'|'charged_points'>} 画哪个度量。 */
  metric: { type: String, required: true },
})

const emit = defineEmits(['select'])

const { t } = useI18n()
// 主题读进 computed：切明暗时 option 才会重算（ECharts 不认 CSS 变量）。
const { theme: themeName } = useAdminTheme()

/** 单个桶不许撑满整张图。 */
const BAR_MAX_WIDTH = 48

const option = computed(() => {
  const theme = chartTheme(themeName.value)
  return {
    ...chartBase(theme),
    grid: { top: 16, left: 8, right: 16, bottom: 8, containLabel: true },
    xAxis: {
      ...categoryAxis(theme),
      data: props.items.map((item) => item.time),
      // interval: 0 —— 每一格都出标签；桶多到挤不下时 echarts 自己隐掉重叠的那些。
      axisLabel: { color: theme.textMuted, interval: 0, hideOverlap: true, formatter: axisLabelOf },
    },
    yAxis: {
      ...valueAxis(theme),
      axisLabel: { color: theme.textMuted, formatter: (value) => formatMetric(props.metric, value) },
    },
    tooltip: { ...chartBase(theme).tooltip, formatter: tooltipOf },
    series: [
      {
        type: 'bar',
        name: t(`visualGameAdmin.llm.cost.metric.${props.metric}`),
        barMaxWidth: BAR_MAX_WIDTH,
        itemStyle: { borderRadius: [4, 4, 0, 0] },
        data: props.items.map((item) => ({
          value: item[props.metric],
          // 这个桶里有成本待核算的调用 —— 它的成本柱子是**不完整**的。vg-studio 的 warning 和 primary
          // 都是金色系，只换颜色几乎看不出来，所以再打一层斜纹：颜色靠不住的时候纹理还在。
          itemStyle: item.unpriced_calls > 0
            ? { color: theme.warning, decal: hatch(theme.surface) }
            : { color: theme.bar },
        })),
      },
    ],
  }
})

/**
 * 轴上不重复年份和日期：按天的桶是 `2026-07-13` → `07-13`，按小时的是 `2026-07-13 09:00` → `09:00`。
 * @param {string} time
 */
function axisLabelOf(time) {
  return time.length > 10 ? time.slice(11) : time.slice(5)
}

/** 45° 斜纹，用底色画在柱子上（不引入新色）。 @param {string} color */
function hatch(color) {
  return { color, dashArrayX: [1, 0], dashArrayY: [2, 5], rotation: -Math.PI / 4, symbolSize: 1 }
}

/** @param {any[]} params */
function tooltipOf(params) {
  const item = props.items[params[0].dataIndex]
  const lines = [
    `<strong>${item.time}</strong>`,
    `${t('visualGameAdmin.llm.cost.metric.cost')}: ${formatMoney(item.supplier_cost_micros)}`,
    `${t('visualGameAdmin.llm.cost.metric.calls')}: ${formatInt(item.calls)}`,
    `${t('visualGameAdmin.llm.cost.metric.charged_points')}: ${formatInt(item.charged_points)}`,
    `${t('visualGameAdmin.llm.cost.card.failedCalls')}: ${formatInt(item.failed_calls)}`,
  ]
  if (item.unpriced_calls > 0) {
    lines.push(`⚠ ${t('visualGameAdmin.llm.cost.unpriced.short', { count: item.unpriced_calls })}`)
  }
  lines.push(t('visualGameAdmin.llm.cost.chart.clickToZoom'))
  return lines.join('<br/>')
}

/** 点一根柱子 → 把整个看板的区间收到这个桶上（陡升那天点开，就只看那一天）。 */
function onSelect({ index }) {
  emit('select', props.items[index])
}
</script>

<template>
  <EChart
    :option="option"
    height="300px"
    @select="onSelect"
  />
</template>
